import { useRef, useEffect, useState } from 'react';
import { useSiteText } from '@/lib/SiteTextContext';
import { useSiteImages } from '@/lib/SiteImagesContext';
import { useReducedMotion, useIsTouch } from '@/hooks/useScroll';
import { MagneticButton } from './MagneticButton';

export function HeroSection() {
  const { t } = useSiteText();
  const { img } = useSiteImages();
  const ref = useRef<HTMLElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const [parallax, setParallax] = useState({ bg: 0, content: 0, glow: 0 });
  const [entered, setEntered] = useState(false);
  const [particles, setParticles] = useState<{ left: number; top: number; size: number; dur: number; delay: number }[]>([]);
  const reduced = useReducedMotion();
  const isTouch = useIsTouch();

  // Cinematic entrance: trigger staggered reveal shortly after mount.
  useEffect(() => {
    const id = window.setTimeout(() => setEntered(true), reduced ? 0 : 120);
    return () => window.clearTimeout(id);
  }, [reduced]);

  // Stable particle field (generated once to avoid layout thrash).
  useEffect(() => {
    if (reduced) return;
    setParticles(
      Array.from({ length: 18 }, () => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: Math.random() * 4 + 2,
        dur: 8 + Math.random() * 10,
        delay: Math.random() * 5,
      }))
    );
  }, [reduced]);

  // Parallax + mouse-driven "wind" glow.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let glowFrame: number | null = null;
    const handleMouseMove = (e: MouseEvent) => {
      if (isTouch) return;
      const glow = glowRef.current;
      if (!glow) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (glowFrame !== null) cancelAnimationFrame(glowFrame);
      glowFrame = requestAnimationFrame(() => {
        glow.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      });
    };

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const heroH = el.offsetHeight || window.innerHeight;
        const progress = Math.min(scrollY / heroH, 1);
        setParallax({
          bg: scrollY * 0.32,
          content: progress * 120,
          glow: progress * 40,
        });
        ticking = false;
      });
    };

    el.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isTouch]);

  const enterClass = `hero-enter ${entered ? 'hero-enter-ready' : ''}`;

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex items-center justify-center overflow-hidden text-white"
    >
      {/* Background image — Ken-Burns cinematic push */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${img('heroBg')}')`,
          transform: `translateY(${parallax.bg}px) scale(${1 + parallax.glow * 0.001})`,
        }}
      >
        <div className={`absolute inset-0 ${reduced ? '' : 'ken-burns'}`} style={{ backgroundImage: `url('${img('heroBg')}')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      </div>

      {/* Depth overlays — ocean gradient + drifting light */}
      <div className="absolute inset-0 bg-gradient-to-b from-ocean-blue/70 via-ocean-blue-dark/55 to-ocean-blue/85" />
      <div className="absolute inset-0 wave-overlay opacity-20" />
      <div className={`absolute inset-0 pointer-events-none opacity-40 mix-blend-soft-light ${reduced ? '' : 'drift'}`} style={{ background: 'radial-gradient(60% 50% at 30% 20%, hsla(200,90%,85%,0.35), transparent 70%)' }} />

      {/* Floating "spray" particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/15"
            style={{
              width: `${p.size}px`,
              height: `${p.size}px`,
              left: `${p.left}%`,
              top: `${p.top}%`,
              animation: `float ${p.dur}s ease-in-out infinite`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Wind spotlight that follows the cursor */}
      {!isTouch && (
        <div
          ref={glowRef}
          className="absolute pointer-events-none rounded-full bg-white/10"
          style={{
            width: '640px',
            height: '640px',
            left: 0,
            top: 0,
            transform: 'translate(50vw, 50vh) translate(-50%, -50%)',
            opacity: 0.12,
            filter: 'blur(110px)',
            willChange: 'transform',
            transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
          }}
        />
      )}

      {/* Foreground animated wave (sail-inspired) */}
      <div className="absolute bottom-0 left-0 w-full h-40 opacity-40 pointer-events-none" style={{ transform: `translateY(${parallax.content * 0.25}px)` }}>
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-full">
          <path fill="white" fillOpacity="0.12">
            <animate
              attributeName="d"
              dur="22s"
              repeatCount="indefinite"
              values="M0,60 C180,20 260,100 500,80 C700,65 850,35 1200,50 V120 H0 Z;M0,70 C180,30 260,110 500,90 C700,75 850,45 1200,60 V120 H0 Z;M0,60 C180,20 260,100 500,80 C700,65 850,35 1200,50 V120 H0 Z"
            />
          </path>
        </svg>
      </div>

      {/* Content — staggered cinematic entrance */}
      <div
        className="relative z-10 text-center px-6 max-w-5xl"
        style={{ transform: `translateY(${parallax.content}px)`, opacity: 1 - parallax.content / 200 }}
      >
        <p className={`${enterClass} text-sm tracking-[0.3em] uppercase mb-6 text-misty-blue font-light`} style={{ transitionDelay: `${reduced ? 0 : 200}ms` }}>
          {t('hero.kicker')}
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold leading-tight mb-8 text-balance">
          <span className={`block ${enterClass}`} style={{ transitionDelay: `${reduced ? 0 : 360}ms` }}>{t('hero.title')}</span>
          <span className={`block text-accent-gold drop-shadow-[0_0_30px_rgba(212,175,55,0.5)] ${enterClass}`} style={{ transitionDelay: `${reduced ? 0 : 520}ms` }}>
            {t('hero.subtitle')}
          </span>
        </h1>
        <p className={`${enterClass} text-lg md:text-xl text-misty-blue/80 font-light max-w-2xl mx-auto leading-relaxed mb-6`} style={{ transitionDelay: `${reduced ? 0 : 680}ms` }}>
          {t('hero.tagline')}
        </p>
        <p className={`${enterClass} text-2xl md:text-3xl font-display font-bold text-center text-white drop-shadow-[0_0_20px_rgba(0,0,0,0.7)] mb-12`} style={{ transitionDelay: `${reduced ? 0 : 800}ms` }}>
          {t('hero.fullName')}
        </p>
        <div className={`${enterClass} flex flex-col sm:flex-row gap-4 justify-center`} style={{ transitionDelay: `${reduced ? 0 : 940}ms` }}>
          <MagneticButton
            onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-shimmer px-8 py-3.5 rounded-full font-medium bg-accent-gold text-ocean-blue hover:bg-accent-gold-dark hover:scale-105 shadow-lg shadow-accent-gold/30 will-change-transform"
          >
            {t('hero.cta1')}
          </MagneticButton>
          <MagneticButton
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-3.5 rounded-full font-medium border-2 border-white/30 hover:border-white/60 hover:bg-white/10 backdrop-blur-sm will-change-transform"
            strength={0.25}
          >
            {t('hero.cta2')}
          </MagneticButton>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
        <div className="w-5 h-8 border-2 border-white/50 rounded-full flex justify-center pt-1.5">
          <div className="w-0.5 h-2 bg-white/50 rounded-full scroll-bounce" />
        </div>
      </div>
    </section>
  );
}
